/**
 * TanStack Query hooks for the Storage admin feature.
 *
 * Wraps the `/api/v1/admin/storage/...` endpoints. Those paths are not yet
 * present in the generated OpenAPI surface, so the hooks cast through
 * `as never` and model the runtime contract as local interfaces (matching
 * the legacy `static/admin/js/pages/storage.js`). TODO: drop the casts once
 * the OpenAPI exports the admin-storage operations.
 *
 * The exception is `useRequestUploadUrl`, which targets the public
 * `/api/v1/storage/upload-url` endpoint (typed by the generated schema).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

/** Bucket privacy mirrors the backend enum (`PRIVATE` / `PUBLIC`). */
export type BucketPrivacy = 'PRIVATE' | 'PUBLIC';

/** One bucket as returned by `GET /admin/storage/buckets`. */
export interface BucketSummary {
  name: string;
  privacy: BucketPrivacy;
  objectCount: number;
  totalSize: number;
  managed?: boolean;
}

/** One object inside a bucket (list item). */
export interface BucketObject {
  key: string;
  size: number;
  contentType?: string;
  lastModified?: string;
  publicUrl: string;
}

/** Page payload of `GET /admin/storage/buckets/{name}/objects`. */
export interface ObjectListPage {
  items: BucketObject[];
  cursor: string | null;
  hasMore: boolean;
}

/** Sensitive-bucket name; the legacy code special-cased this exact string. */
export const SENSITIVE_BUCKET_NAME = 'users-documents';

export type UploadUrlResponse = components['schemas']['UploadUrlResponse'];

/* ------------------------------------------------------------------ */
/* Query keys                                                          */
/* ------------------------------------------------------------------ */

export const storageKeys = {
  all: ['storage'] as const,
  buckets: () => [...storageKeys.all, 'buckets'] as const,
  objects: (bucket: string, cursor: string | null) =>
    [...storageKeys.all, 'objects', bucket, cursor] as const,
  object: (bucket: string, key: string) =>
    [...storageKeys.all, 'object', bucket, key] as const,
};

/* ------------------------------------------------------------------ */
/* Generic openapi-fetch result we get when calling untyped endpoints   */
/* ------------------------------------------------------------------ */

interface RawResult<T> {
  data?: T;
  error?: unknown;
}

/* ------------------------------------------------------------------ */
/* Buckets                                                              */
/* ------------------------------------------------------------------ */

export function useBuckets(): UseQueryResult<BucketSummary[]> {
  return useQuery({
    queryKey: storageKeys.buckets(),
    queryFn: async () => {
      const result = (await api.GET(
        '/api/v1/admin/storage/buckets' as never,
      )) as RawResult<BucketSummary[]>;
      if (result.error) throw result.error as Error;
      return Array.isArray(result.data) ? result.data : [];
    },
  });
}

export interface CreateBucketBody {
  name: string;
  privacy: BucketPrivacy;
}

export function useCreateBucket(): UseMutationResult<void, Error, CreateBucketBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const result = (await api.POST(
        '/api/v1/admin/storage/buckets' as never,
        { body } as never,
      )) as RawResult<unknown>;
      if (result.error) throw result.error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.buckets() });
    },
  });
}

export function useDeleteBucket(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bucketName) => {
      const result = (await api.DELETE(
        '/api/v1/admin/storage/buckets/{name}' as never,
        { params: { path: { name: bucketName } } } as never,
      )) as RawResult<unknown>;
      if (result.error) throw result.error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.buckets() });
    },
  });
}

export interface UpdatePrivacyBody {
  bucketName: string;
  privacy: BucketPrivacy;
  sensitiveAck?: boolean;
}

export function useUpdateBucketPrivacy(): UseMutationResult<void, Error, UpdatePrivacyBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bucketName, privacy, sensitiveAck }) => {
      const body: { privacy: BucketPrivacy; sensitiveAck?: boolean } = { privacy };
      if (sensitiveAck !== undefined) body.sensitiveAck = sensitiveAck;
      const result = (await api.PATCH(
        '/api/v1/admin/storage/buckets/{name}/privacy' as never,
        { params: { path: { name: bucketName } }, body } as never,
      )) as RawResult<unknown>;
      if (result.error) throw result.error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.buckets() });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Objects                                                              */
/* ------------------------------------------------------------------ */

export function useObjectsList(
  bucket: string | null,
  cursor: string | null,
): UseQueryResult<ObjectListPage> {
  return useQuery({
    queryKey: storageKeys.objects(bucket ?? '', cursor),
    queryFn: async () => {
      if (!bucket) {
        return { items: [], cursor: null, hasMore: false };
      }
      const params = new URLSearchParams({ limit: '50' });
      if (cursor) params.set('cursor', cursor);
      const path = `/api/v1/admin/storage/buckets/${encodeURIComponent(bucket)}/objects?${params.toString()}`;
      const result = (await api.GET(path as never)) as RawResult<ObjectListPage>;
      if (result.error) throw result.error as Error;
      const data = result.data ?? { items: [], cursor: null, hasMore: false };
      return {
        items: Array.isArray(data.items) ? data.items : [],
        cursor: data.cursor ?? null,
        hasMore: Boolean(data.hasMore),
      };
    },
    enabled: bucket !== null,
  });
}

export function useObjectDetail(
  bucket: string | null,
  key: string | null,
): UseQueryResult<BucketObject> {
  return useQuery({
    queryKey: storageKeys.object(bucket ?? '', key ?? ''),
    queryFn: async () => {
      if (!bucket || !key) throw new Error('bucket/key obrigatorios');
      const path =
        `/api/v1/admin/storage/buckets/${encodeURIComponent(bucket)}/objects/${encodeURIComponent(key)}`;
      const result = (await api.GET(path as never)) as RawResult<BucketObject>;
      if (result.error) throw result.error as Error;
      if (!result.data) throw new Error('Objeto nao encontrado');
      return result.data;
    },
    enabled: bucket !== null && key !== null,
  });
}

export interface DeleteObjectArgs {
  bucket: string;
  key: string;
}

export function useDeleteObject(): UseMutationResult<void, Error, DeleteObjectArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bucket, key }) => {
      const path =
        `/api/v1/admin/storage/buckets/${encodeURIComponent(bucket)}/objects/${encodeURIComponent(key)}`;
      const result = (await api.DELETE(path as never)) as RawResult<unknown>;
      if (result.error) throw result.error as Error;
    },
    onSuccess: (_void, { bucket }) => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.all });
      void queryClient.invalidateQueries({ queryKey: storageKeys.buckets() });
      void queryClient.invalidateQueries({
        queryKey: [...storageKeys.all, 'objects', bucket],
      });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Upload (presigned URL + direct PUT to MinIO)                         */
/* ------------------------------------------------------------------ */

export interface UploadFileArgs {
  bucket: string;
  file: File;
}

interface UploadUrlRuntime {
  uploadUrl?: string;
  objectKey?: string;
  publicUrl?: string;
  expiresAt?: string;
  requiredHeaders?: Record<string, string>;
}

/** Server-side upload purposes by managed bucket (AUD-004). */
const PURPOSE_BY_BUCKET: Record<string, string> = {
  'users-documents': 'USER_DOCUMENT',
  'product-images': 'PRODUCT_IMAGE',
  'banner-images': 'BANNER_IMAGE',
  'category-icons': 'CATEGORY_ICON',
  'notification-images': 'NOTIFICATION_IMAGE',
};

async function requestUploadUrl(
  purpose: string,
  file: File,
): Promise<UploadUrlRuntime> {
  const result = (await api.POST('/api/v1/storage/upload-url', {
    body: {
      purpose,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      contentLength: file.size,
    } as never,
  })) as RawResult<UploadUrlRuntime>;
  if (result.error) throw result.error as Error;
  if (!result.data?.uploadUrl) {
    throw new Error('Resposta invalida do servidor de upload.');
  }
  return result.data;
}

async function putToMinio(
  uploadUrl: string,
  file: File,
  requiredHeaders?: Record<string, string>,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: requiredHeaders ?? { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!res.ok) {
    throw new Error(`Falha ao enviar arquivo (HTTP ${res.status})`);
  }
}

async function publishUpload(purpose: string, objectKey: string): Promise<void> {
  const path =
    `/api/v1/storage/publish?purpose=${encodeURIComponent(purpose)}` +
    `&objectKey=${encodeURIComponent(objectKey)}`;
  const result = (await api.POST(path as never)) as RawResult<unknown>;
  if (result.error) throw result.error as Error;
}

export function useUploadFile(): UseMutationResult<void, Error, UploadFileArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bucket, file }) => {
      const purpose = PURPOSE_BY_BUCKET[bucket];
      if (!purpose) throw new Error('Bucket sem politica de upload correspondente.');
      const presigned = await requestUploadUrl(purpose, file);
      await putToMinio(presigned.uploadUrl as string, file, presigned.requiredHeaders);
      await publishUpload(purpose, presigned.objectKey as string);
    },
    onSuccess: (_void, { bucket }) => {
      void queryClient.invalidateQueries({ queryKey: storageKeys.buckets() });
      void queryClient.invalidateQueries({
        queryKey: [...storageKeys.all, 'objects', bucket],
      });
    },
  });
}
